import { useEffect, useRef, useState } from "react";
import { DropdownProps, Option } from ".";
import { InputProps } from "@campgladiator/cgui-core.atoms.input";

const useDropdown = ({
  onChangeSelectedOption,
  options,
  placeHolder,
}: {
  onChangeSelectedOption: DropdownProps["onChangeSelectedOption"];
  options: Option[];
  placeHolder?: string;
}) => {
  const placeholderOption: Option = {
    itemId: "",
    type: "item",
    text: placeHolder ?? "Select An Option",
  };
  const [isActive, setIsActive] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] =
    useState<Option>(placeholderOption);
  const hasSelectedValue = selectedOption !== placeholderOption;
  const icon: InputProps["icon"] = hasSelectedValue
    ? { type: "monochrome", name: "times-circle" }
    : { name: "icon-angle-down", type: "line" };

  const clearSelectedOption = () => {
    setSelectedOption(placeholderOption);
    onChangeSelectedOption(undefined);
  };
  const onIconClick: InputProps["onIconClick"] = () =>
    hasSelectedValue ? clearSelectedOption() : setIsActive(!isActive);

  const contentRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listMenuRef = useRef<HTMLDivElement>(null);

  const formattedOptionsForItemList = (options) =>
    options.map((option) => formatClickableMenuItem(option));

  const formatClickableMenuItem = (option) => ({
    ...option,
    handleClick: () => {
      setSelectedOption(option);
      onChangeSelectedOption(option);
      setIsActive(false);
    },
  });

  const handleFocus = () => {
    inputRef.current.focus();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      contentRef.current &&
      !contentRef.current.contains(event.target as HTMLElement)
    ) {
      setIsActive(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contentRef]);

  useEffect(() => {
    const selectedOption = options.find((opt) => opt.isDefault);
    if (selectedOption) setSelectedOption(selectedOption);
    else setSelectedOption(placeholderOption);
  }, [options]);

  return {
    contentRef,
    formattedOptionsForItemList,
    handleFocus,
    icon,
    inputRef,
    isActive,
    listMenuRef,
    onIconClick,
    selectedOption,
    setIsActive,
  };
};

export default useDropdown;
